import { useMemo, useState } from 'react';
import { Button, Collapse, Stack } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa6';
import { IWorkerAssignment } from '@company/types/workerAssignment';
import { JobQuestionResponseModel } from '@th-types/job-question-response-model.type';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import { JobApplication } from '@th-types/job-application.type';
import { IWorkerData } from '@th-types/worker.type';
import { Panel } from '@components/elements';
import JobQuestionComponent from '@company/components/JobWorker/JobQuestionComponent';
import { JobAssignmentStatus } from '@th-types/job-assignment-status.type';
import ApplicationInfoHistory from './ApplicationInfoHistory';
import ApplicationInfoHeader from './ApplicationInfoHeader';

interface ApplicationInfoProps {
  worker: IWorkerData;
  jobQuestionResponseList?: JobQuestionResponseModel[];
  workerAssignment: IWorkerAssignment;
  jobApplication?: JobApplication;
}

export default function ApplicationInfo({
  worker,
  jobQuestionResponseList,
  workerAssignment,
  jobApplication,
}: ApplicationInfoProps) {
  const jobAssignmentStatus = useJobAssignmentStatus();
  const { isHired } = jobAssignmentStatus(
    workerAssignment.thJobAssignment?.status
  );
  const [showQuestions, setShowQuestions] = useState(false);

  const showWorkerInfo = () => {
    return jobApplication || isHired();
  };

  const applicationDate = useMemo(() => {
    const workerInterestedItems =
      workerAssignment.thJobAssignmentLogList?.filter(
        (item) =>
          item.toStatus === JobAssignmentStatus.WORKER_INTERESTED ||
          item.toStatus === JobAssignmentStatus.WORKER_APPLIED
      );

    if (!workerInterestedItems || workerInterestedItems.length === 0) {
      return undefined;
    }

    const oldestItem = workerInterestedItems.reduce((oldest, current) =>
      new Date(current.dateCreated) < new Date(oldest.dateCreated)
        ? current
        : oldest
    );

    return oldestItem.dateCreated;
  }, [workerAssignment.thJobAssignmentLogList]);

  return (
    <div className="mt-3">
      {showWorkerInfo() && (
        <Panel>
          <ApplicationInfoHeader
            jobAssignment={workerAssignment.thJobAssignment}
            jobApplication={jobApplication}
            applicationDate={applicationDate}
            worker={worker}
          />
          {jobQuestionResponseList && (
            <>
              <Button
                type="button"
                size="sm"
                onClick={() => setShowQuestions(!showQuestions)}
                className="d-flex justify-content-center align-items-center gap-2 mt-3 mx-auto"
              >
                {showQuestions ? (
                  <>
                    <b>Collapse Questions</b>
                    <FaChevronUp />
                  </>
                ) : (
                  <>
                    <b>Expand Questions</b>
                    <FaChevronDown />
                  </>
                )}
              </Button>
              <Collapse in={showQuestions}>
                <Stack>
                  {jobQuestionResponseList.map((jobQuestionResponse) => {
                    return (
                      <JobQuestionComponent
                        jobQuestionResponseModel={jobQuestionResponse}
                        key={`job-question-${jobQuestionResponse.questionId}`}
                      />
                    );
                  })}
                </Stack>
              </Collapse>
            </>
          )}
        </Panel>
      )}
      <div className="mt-3">
        <ApplicationInfoHistory
          thJobAssignmentLogList={workerAssignment.thJobAssignmentLogList}
        />
      </div>
    </div>
  );
}
